import { useEffect, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import { usePrescriptionSave } from "../context/PrescriptionSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStaticOptions } from "../context/StaticOptionsContext";
import { dataService } from "../services/dataService";

const TartozekValaszto = () => {
	const { order, updateOrder } = useOrder();
	const { staticOptions, loading: optionsLoading, error } = useStaticOptions();
	const navigate = useStepNavigate();
	const [loading, setLoading] = useState(false);
	const { savePrescription, loading: prescriptionSaveLoading } = usePrescriptionSave();
	const singleShoeOnlyComponents = new Set(["ttt", "j-15"]);

	const components = staticOptions?.components || [];
	const aktVeny = order.venyek[order.aktivVenyIndex] || {};

	const getSelectedOptionForComponent = (componentName) => {
		const venyComponents = Array.isArray(aktVeny.components) ? aktVeny.components : [];
		const balComponent = venyComponents.find((c) => c.aidfinance_national === componentName && c.is_left === 1);
		const jobbComponent = venyComponents.find((c) => c.aidfinance_national === componentName && c.is_left === 0);

		if (balComponent && jobbComponent) return "Mindkettő";
		if (balComponent) return "Bal";
		if (jobbComponent) return "Jobb";
		return "";
	};

	const [selectedOptions, setSelectedOptions] = useState(() => {
		const initial = {};
		components.forEach((comp) => {
			initial[comp.aidfinance_national] = getSelectedOptionForComponent(comp.aidfinance_national);
		});
		return initial;
	});

	useEffect(() => {
		const updated = {};
		components.forEach((comp) => {
			updated[comp.aidfinance_national] = getSelectedOptionForComponent(comp.aidfinance_national);
		});
		setSelectedOptions(updated);
	}, [order.aktivVenyIndex, components.length]);

	const handleOptionSelected = (componentName, option) => {
		if (
			option === "Mindkettő" &&
			singleShoeOnlyComponents.has((componentName || "").trim().toLocaleLowerCase("hu-HU"))
		) {
			return;
		}

		const newOption = selectedOptions[componentName] === option ? "" : option;

		const newSelectedOptions = {
			...selectedOptions,
			[componentName]: newOption,
		};
		setSelectedOptions(newSelectedOptions);

		const component = components.find((c) => c.aidfinance_national === componentName);
		if (!component) return;

		let venyComponents = Array.isArray(aktVeny.components) ? [...aktVeny.components] : [];

		venyComponents = venyComponents.filter((c) => c.aidfinance_national !== componentName);

		if (newOption === "Bal") {
			venyComponents.push({
				component_szam: component.component_szam,
				aidfinance_szam: component.aidfinance_szam,
				aidfinance_code: component.aidfinance_code,
				aidfinance_national: component.aidfinance_national,
				is_left: 1,
			});
		} else if (newOption === "Jobb") {
			venyComponents.push({
				component_szam: component.component_szam,
				aidfinance_szam: component.aidfinance_szam,
				aidfinance_code: component.aidfinance_code,
				aidfinance_national: component.aidfinance_national,
				is_left: 0,
			});
		} else if (newOption === "Mindkettő") {
			venyComponents.push(
				{
					component_szam: component.component_szam,
					aidfinance_szam: component.aidfinance_szam,
					aidfinance_code: component.aidfinance_code,
					aidfinance_national: component.aidfinance_national,
					is_left: 1,
				},
				{
					component_szam: component.component_szam,
					aidfinance_szam: component.aidfinance_szam,
					aidfinance_code: component.aidfinance_code,
					aidfinance_national: component.aidfinance_national,
					is_left: 0,
				}
			);
		}

		const venyek = [...order.venyek];
		venyek[order.aktivVenyIndex] = {
			...venyek[order.aktivVenyIndex],
			components: venyComponents,
		};
		updateOrder({
			...order,
			venyek,
		});
	};

	const onefoot = aktVeny.is_onefoot_meas;
	const isLeft = aktVeny.is_left;

	const getTakenSidesForComponent = (aidfinance_national) => {
		const taken = { Bal: false, Jobb: false };
		order.venyek.forEach((veny, idx) => {
			if (idx === order.aktivVenyIndex) return;
			const comps = Array.isArray(veny.components) ? veny.components : [];
			comps.forEach((c) => {
				if (c.aidfinance_national === aidfinance_national) {
					if (c.is_left === 1) taken.Bal = true;
					if (c.is_left === 0) taken.Jobb = true;
				}
			});
		});
		return taken;
	};

	const isOptionDisabled = (option, aidfinance_national) => {
		if (
			option === "Mindkettő" &&
			singleShoeOnlyComponents.has((aidfinance_national || "").trim().toLocaleLowerCase("hu-HU"))
		) {
			return true;
		}

		if (onefoot === 1) {
			// Ha csak egy láb mérhető, tiltsuk le a nem megfelelő opciókat
			if (isLeft === 1 && option !== "Bal") return true; // Csak "Bal" választható
			if (isLeft === 0 && option !== "Jobb") return true; // Csak "Jobb" választható
		}

		const taken = getTakenSidesForComponent(aidfinance_national);

		// Ha az adott oldal már foglalt, tiltsuk le
		if (option === "Bal" && taken.Bal) return true;
		if (option === "Jobb" && taken.Jobb) return true;

		// Ha mindkét oldal foglalt, tiltsuk le a "Mindkettő" opciót
		if (option === "Mindkettő" && (taken.Bal || taken.Jobb)) return true;

		return false; // Ha egyik feltétel sem teljesül, az opció választható
	};

	const handleBackClick = () => {
		navigate("/VenyesTartozek");
	};

	const saveActualPrescription = async () => {
		try {
			setLoading(true);
			const aktVeny = order.venyek[order.aktivVenyIndex];
			const data = {
				hmeliorder_szam: order.hmeliorder?.hmeliorder_szam,
				veny: aktVeny,
			};

			const result = await savePrescription(data);
			if (result?.success) {
				navigate("/VenyekLista");
			}

			setLoading(false);
		} catch (error) {
			console.error("Hiba a vény mentésekor:", error);
			setLoading(false);
		}
	};

	const handleNextClick = async () => {
		await saveActualPrescription();
		if (order.hmeliorder?.hmeliorder_type === 2) {
			navigate("/BetetTartozek");
		} else {
			navigate("/VenyekLista");
		}
	};

	if (optionsLoading) {
		return <div className="text-center my-5">Tartozékok betöltése...</div>;
	}
	if (error) {
		return <div className="text-danger my-5">Hiba a tartozékok betöltésekor!</div>;
	}

	return (
		<TablerLayout title="Kérjük válassza ki a kívánt vényes tartozékokat!">
			<div className="row justify-content-center">
				<div className="col-md-10 mb-4">
					<div className="container-xl">
						<div style={{ textAlign: "right", fontWeight: 500, fontSize: "1.1rem", marginBottom: 8 }}>
							{order.aktivVenyIndex + 1} / {order.venyek.length}. vény
						</div>
					</div>
					<div className="card-body">
						<div className="row">
							{/* Bal oldali oszlop */}
							<div className="col-12 col-md-5">
								{components.slice(0, Math.ceil(components.length / 2)).map((tartozek) => (
									<div className="mb-5" key={tartozek.aidfinance_szam}>
										<div className="mb-3 fs-5 fw-semibold text-center">{tartozek.aidfinance_national}</div>
										<div className="d-flex flex-column flex-md-row gap-2 gap-md-3 justify-content-center align-items-stretch">
											{["Bal", "Mindkettő", "Jobb"].map((option) => (
												<button
													key={option}
													type="button"
													className={`btn btn-lg w-100 w-md-auto px-2 py-2
													${selectedOptions[tartozek.aidfinance_national] === option ? "btn-primary shadow" : "btn-outline-primary bg-white text-primary border-2"}`}
													style={{
														fontWeight: 500,
														fontSize: 18,
														whiteSpace: "normal",
														wordBreak: "break-word"
													}}
													onClick={() => handleOptionSelected(tartozek.aidfinance_national, option)}
													disabled={isOptionDisabled(option, tartozek.aidfinance_national)}
												>
													{option}
												</button>
											))}
										</div>
									</div>
								))}
							</div>

							{/* Középső elválasztó oszlop */}
							<div className="col-md-2 d-none d-md-block">
								<div style={{
									width: '1px',
									backgroundColor: '#dee2e6',
									height: '100%',
									margin: '0 auto'
								}} />
							</div>

							{/* Jobb oldali oszlop */}
							<div className="col-12 col-md-5">
								{components.slice(Math.ceil(components.length / 2)).map((tartozek) => (
									<div className="mb-5" key={tartozek.aidfinance_szam}>
										<div className="mb-3 fs-5 fw-semibold text-center">{tartozek.aidfinance_national}</div>
										<div className="d-flex flex-column flex-md-row gap-2 gap-md-3 justify-content-center align-items-stretch">
											{["Bal", "Mindkettő", "Jobb"].map((option) => (
												<button
													key={option}
													type="button"
													className={`btn btn-lg w-100 w-md-auto px-2 py-2
													${selectedOptions[tartozek.aidfinance_national] === option ? "btn-primary shadow" : "btn-outline-primary bg-white text-primary border-2"}`}
													style={{
														fontWeight: 500,
														fontSize: 18,
														whiteSpace: "normal",
														wordBreak: "break-word"
													}}
													onClick={() => handleOptionSelected(tartozek.aidfinance_national, option)}
													disabled={isOptionDisabled(option, tartozek.aidfinance_national)}
												>
													{option}
												</button>
											))}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
			<StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
			<StepperNavButton side="right" label="Vény mentése" onClick={handleNextClick} />
		</TablerLayout>
	);
};

export default TartozekValaszto;
